import type { Query } from '@tanstack/react-query';
import { Id } from '@hero/schema';
import { DEFAULT_QUERY_OPTIONS, ENTITY_CONFIGS, type Entity } from '../config';
import type { EntityCacheConfig, QueryDefaultsOptions } from '../types';
import { getEntityConfig } from './entity';

/**
 * Resolves the effective `enabled` for a query.
 * Consumer (apiConfig.enabled) takes priority; otherwise use the hook's isEnabled() or the default.
 */
export function resolveEnabled(
  apiConfigEnabled: boolean | undefined,
  isEnabledFn: (() => boolean) | undefined,
  defaultWhenNeither: boolean = true,
): boolean {
  if (apiConfigEnabled !== undefined) {
    return apiConfigEnabled;
  }
  return isEnabledFn?.() ?? defaultWhenNeither;
}

type QueryEnabledConfig = {
  enabled?: boolean;
  disableIfNotAuth?: boolean;
};

/**
 * Composes `enabled` / hook `isEnabled` with optional `disableIfNotAuth`.
 * Used by `useQueryAPI` and `useInfiniteQueryAPI`.
 */
export function resolveQueryEnabled(
  apiConfig: QueryEnabledConfig | undefined,
  isEnabledFn: (() => boolean) | undefined,
  isAuthenticated: boolean,
): boolean {
  const base = resolveEnabled(apiConfig?.enabled, isEnabledFn);
  if (apiConfig?.disableIfNotAuth) {
    return base && isAuthenticated;
  }
  return base;
}

export const getOneQueryKey = (entity: Entity, id: Id | undefined) => {
  const { singular } = getEntityConfig(entity);

  if (id) {
    return [singular, id];
  }

  return [singular];
};

/**
 * Stable cache key from flat get-one params. Use when the request is not just { _id }.
 * - If params has only _id (and it's set): returns getOneQueryKey(entity, params._id).
 * - Otherwise: returns [singular, 'one', stable JSON string of params] (key order sorted).
 */
export function getQueryKeyFromParams(
  entity: Entity,
  params: Record<string, unknown> | undefined,
): unknown[] {
  if (!params || Object.keys(params).length === 0) {
    return getOneQueryKey(entity, undefined);
  }
  const keys = Object.keys(params);
  if (keys.length === 1 && keys[0] === '_id' && params._id != null) {
    return getOneQueryKey(entity, params._id as Id);
  }
  const { singular } = getEntityConfig(entity);
  const sorted = keys.sort().reduce<Record<string, unknown>>((acc, k) => {
    acc[k] = params[k];
    return acc;
  }, {});
  return [singular, 'one', JSON.stringify(sorted)];
}

/**
 * Returns query key for list/infinite queries.
 * Uses entity plural + serialized query (limit + filters); skip is omitted so the key is stable across pages.
 */
export function getListQueryKey(entity: Entity, query: Record<string, unknown>): unknown[] {
  const { plural } = getEntityConfig(entity);
  const { skip: _skip, ...rest } = query as Record<string, unknown> & {
    skip?: number;
  };
  return [plural, rest];
}

/**
 * Returns query default options for useQuery/useInfiniteQuery.
 * Pass an entity to use per-entity staleTime/gcTime from ENTITY_CONFIGS when set.
 */
export function getQueryDefaults(entity?: Entity): QueryDefaultsOptions {
  if (!entity) {
    return { ...DEFAULT_QUERY_OPTIONS };
  }
  const config = ENTITY_CONFIGS[entity] as EntityCacheConfig | undefined;
  if (!config) {
    return { ...DEFAULT_QUERY_OPTIONS };
  }
  return {
    ...DEFAULT_QUERY_OPTIONS,
    ...(config.staleTime !== undefined && { staleTime: config.staleTime }),
    ...(config.gcTime !== undefined && { gcTime: config.gcTime }),
    ...(config.refetchOnWindowFocus !== undefined && {
      refetchOnWindowFocus: config.refetchOnWindowFocus,
    }),
    ...(config.refetchInterval !== undefined && {
      refetchInterval: config.refetchInterval,
    }),
    ...(config.retry !== undefined && { retry: config.retry }),
  };
}

// Cast through `EntityCacheConfig[]` so the `c.persist` narrow access works
// regardless of the literal-union shape produced by the `as const satisfies`
// declaration in `ENTITY_CONFIGS`.
const _persistKeys: string[] = (Object.values(ENTITY_CONFIGS) as Array<{
  persist?: boolean;
  singular: string;
  plural: string;
}>)
  .filter(c => c.persist)
  .flatMap(c => [c.singular, c.plural]);

/**
 * Returns true if the query belongs to an entity with `persist: true` in ENTITY_CONFIGS.
 * Use in each app's `cachingQueries` alongside app-specific keys.
 */
export function shouldPersistQuery(query: Query): boolean {
  return query.queryKey.some(k => typeof k === 'string' && _persistKeys.includes(k));
}
